import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
  QueryConstraint,
  Unsubscribe,
  writeBatch,
  runTransaction
} from 'firebase/firestore';
import { db, useMockData } from './config';
import { Project, Phase, Task, Ticket, Template, User, FileAsset, Comment, TaskStatus, PhaseStatus, CustomCalendar, ProjectRole, Role } from '../../types';
import { handleError } from '../errors/handler';

// Helper to convert Firestore timestamps to numbers
const timestampToNumber = (timestamp: any): number => {
  if (timestamp?.toMillis) {
    return timestamp.toMillis();
  }
  if (timestamp instanceof Date) {
    return timestamp.getTime();
  }
  return timestamp || Date.now();
};

// Helper to convert numbers to Firestore timestamps
const numberToTimestamp = (num: number | undefined): Timestamp | undefined => {
  if (!num) return undefined;
  return Timestamp.fromMillis(num);
};

// Retry logic for transient failures
const withRetry = async <T>(
  operation: () => Promise<T>,
  operationName: string,
  maxRetries = 3,
  backoff = 1000
): Promise<T> => {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error: any) {
      const isTransient = 
        error.code === 'unavailable' || 
        error.code === 'deadline-exceeded' ||
        error.code === 'resource-exhausted';
      
      const isLastAttempt = attempt === maxRetries - 1;
      
      if (!isTransient || isLastAttempt) {
        console.error(`${operationName} failed after ${attempt + 1} attempt(s):`, error);
        throw error;
      }
      
      // Exponential backoff
      const delay = backoff * Math.pow(2, attempt);
      console.warn(`${operationName} failed (attempt ${attempt + 1}), retrying in ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  throw new Error(`${operationName} failed after ${maxRetries} retries`);
};

// Sentry breadcrumb helper
const addSentryBreadcrumb = (message: string, data?: Record<string, any>) => {
  try {
    // Dynamic import to avoid errors if Sentry is not configured
    const Sentry = (window as any).Sentry;
    if (Sentry && Sentry.addBreadcrumb) {
      Sentry.addBreadcrumb({
        category: 'firestore',
        message,
        level: 'info',
        data
      });
    }
  } catch (error) {
    // Silently fail if Sentry is not available
  }
};

// PROJECTS
export const fetchProjects = async (userId?: string, role?: Role): Promise<Project[]> => {
  if (useMockData || !db) return [];
  
  try {
    // If PM/Admin role, fetch projects where they're assigned as PM
    if (role === Role.PM || role === Role.ADMIN) {
      if (userId) {
        // Query projects where user is Brand PM OR Factory PM
        // Note: Firestore doesn't support OR in a single query, so we need to:
        // 1. Query brandPMId === userId
        // 2. Query factoryPMId === userId
        // 3. Merge results (deduplicate by id)
        
        const brandPMQuery = query(
          collection(db, 'projects'),
          where('brandPMId', '==', userId),
          orderBy('startDate', 'desc')
        );
        const factoryPMQuery = query(
          collection(db, 'projects'),
          where('factoryPMId', '==', userId),
          orderBy('startDate', 'desc')
        );
        
        const [brandSnapshot, factorySnapshot] = await Promise.all([
          getDocs(brandPMQuery),
          getDocs(factoryPMQuery)
        ]);
        
        const projectMap = new Map<string, Project>();
        
        brandSnapshot.docs.forEach(doc => {
          projectMap.set(doc.id, {
            id: doc.id,
            ...doc.data(),
            startDate: doc.data().startDate || '',
            launchDate: doc.data().launchDate || '',
          } as Project);
        });
        
        factorySnapshot.docs.forEach(doc => {
          if (!projectMap.has(doc.id)) {
            projectMap.set(doc.id, {
              id: doc.id,
              ...doc.data(),
              startDate: doc.data().startDate || '',
              launchDate: doc.data().launchDate || '',
            } as Project);
          }
        });
        
        return Array.from(projectMap.values());
      }
    }
    
    // Original logic for regular users (assignedUserIds)
    const constraints: QueryConstraint[] = [];
    if (userId) {
      constraints.push(where('assignedUserIds', 'array-contains', userId));
    }
    constraints.push(orderBy('startDate', 'desc'));
    
    const q = query(collection(db, 'projects'), ...constraints);
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      startDate: doc.data().startDate || '',
      launchDate: doc.data().launchDate || '',
    })) as Project[];
  } catch (error) {
    handleError(error);
    return [];
  }
};

export const getProject = async (projectId: string): Promise<Project | null> => {
  if (useMockData || !db) return null;
  
  try {
    const docSnap = await getDoc(doc(db, 'projects', projectId));
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as Project;
    }
    return null;
  } catch (error) {
    console.error('Error fetching project:', error);
    return null;
  }
};

export const createProject = async (project: Omit<Project, 'id'>): Promise<string> => {
  if (useMockData || !db) throw new Error('Firebase not configured');
  
  try {
    const projectRef = doc(collection(db, 'projects'));
    await setDoc(projectRef, {
      ...project,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    return projectRef.id;
  } catch (error) {
    handleError(error);
    throw error;
  }
};

export const updateProject = async (projectId: string, updates: Partial<Project>): Promise<void> => {
  if (useMockData || !db) throw new Error('Firebase not configured');
  
  try {
    await updateDoc(doc(db, 'projects', projectId), {
      ...updates,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    handleError(error);
    throw error;
  }
};

export const deleteProject = async (projectId: string): Promise<void> => {
  if (useMockData || !db) throw new Error('Firebase not configured');
  
  await deleteDoc(doc(db, 'projects', projectId));
};

// PHASES
export const fetchPhases = async (projectId?: string, templateId?: string): Promise<Phase[]> => {
  if (useMockData || !db) return [];
  
  try {
    const constraints: QueryConstraint[] = [];
    if (projectId) {
      constraints.push(where('projectId', '==', projectId));
    }
    if (templateId) {
      constraints.push(where('templateId', '==', templateId));
    }
    constraints.push(orderBy('sequence', 'asc'));
    
    const q = query(collection(db, 'phases'), ...constraints);
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as Phase[];
  } catch (error) {
    console.error('Error fetching phases:', error);
    return [];
  }
};

export const createPhase = async (phase: Omit<Phase, 'id'>): Promise<string> => {
  if (useMockData || !db) throw new Error('Firebase not configured');
  
  const phaseRef = doc(collection(db, 'phases'));
  await setDoc(phaseRef, phase);
  return phaseRef.id;
};

export const updatePhase = async (phaseId: string, updates: Partial<Phase>): Promise<void> => {
  if (useMockData || !db) throw new Error('Firebase not configured');
  
  await updateDoc(doc(db, 'phases', phaseId), updates);
};

// TASKS
export const fetchTasks = async (projectId?: string, phaseId?: string, parentId?: string, templateId?: string): Promise<Task[]> => {
  if (useMockData || !db) return [];
  
  try {
    const constraints: QueryConstraint[] = [];
    
    // Safety check: prevent fetching ALL tasks without filters (security and performance)
    if (!projectId && !phaseId && !templateId && parentId === undefined) {
      console.warn('[fetchTasks] Called without any filters - returning empty array to prevent fetching all tasks');
      return [];
    }
    
    if (projectId) {
      constraints.push(where('projectId', '==', projectId));
    }
    if (templateId) {
      constraints.push(where('templateId', '==', templateId));
      // Template tasks must have projectId explicitly set to null
      constraints.push(where('projectId', '==', null));
    }
    if (phaseId) {
      constraints.push(where('phaseId', '==', phaseId));
    }
    if (parentId !== undefined) {
      if (parentId === null) {
        constraints.push(where('parentId', '==', null));
      } else {
        constraints.push(where('parentId', '==', parentId));
      }
    }
    
    const q = query(collection(db, 'tasks'), ...constraints);
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        lastActivityAt: timestampToNumber(data.lastActivityAt),
        startedAt: data.startedAt ? timestampToNumber(data.startedAt) : undefined,
        completedAt: data.completedAt ? timestampToNumber(data.completedAt) : undefined,
        lastNudgedAt: data.lastNudgedAt ? timestampToNumber(data.lastNudgedAt) : undefined,
        followUpDismissedAt: data.followUpDismissedAt ? timestampToNumber(data.followUpDismissedAt) : undefined,
      };
    }) as Task[];
  } catch (error) {
    console.error('Error fetching tasks:', error);
    return [];
  }
};

export const getTask = async (taskId: string): Promise<Task | null> => {
  if (useMockData || !db) return null;
  
  try {
    const docSnap = await getDoc(doc(db, 'tasks', taskId));
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        lastActivityAt: timestampToNumber(data.lastActivityAt),
        startedAt: data.startedAt ? timestampToNumber(data.startedAt) : undefined,
        completedAt: data.completedAt ? timestampToNumber(data.completedAt) : undefined,
        lastNudgedAt: data.lastNudgedAt ? timestampToNumber(data.lastNudgedAt) : undefined,
        followUpDismissedAt: data.followUpDismissedAt ? timestampToNumber(data.followUpDismissedAt) : undefined,
      } as Task;
    }
    return null;
  } catch (error) {
    console.error('Error fetching task:', error);
    return null;
  }
};

// Fetch only tasks assigned to user
export const fetchMyAssignedTasks = async (userId: string): Promise<Task[]> => {
  if (useMockData || !db) return [];
  
  try {
    const q = query(
      collection(db, 'tasks'),
      where('assigneeIds', 'array-contains', userId)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        lastActivityAt: timestampToNumber(data.lastActivityAt),
        startedAt: data.startedAt ? timestampToNumber(data.startedAt) : undefined,
        completedAt: data.completedAt ? timestampToNumber(data.completedAt) : undefined,
        lastNudgedAt: data.lastNudgedAt ? timestampToNumber(data.lastNudgedAt) : undefined,
        followUpDismissedAt: data.followUpDismissedAt ? timestampToNumber(data.followUpDismissedAt) : undefined,
      };
    }) as Task[];
  } catch (error) {
    console.error('Error fetching my assigned tasks:', error);
    return [];
  }
};

export const createTask = async (task: Omit<Task, 'id'>): Promise<string> => {
  if (useMockData || !db) throw new Error('Firebase not configured');
  
  const taskRef = doc(collection(db, 'tasks'));
  const taskData: any = {
    ...task,
    lastActivityAt: Timestamp.now(),
  };
  
  if (task.startedAt) taskData.startedAt = numberToTimestamp(task.startedAt);
  if (task.completedAt) taskData.completedAt = numberToTimestamp(task.completedAt);
  if (task.lastNudgedAt) taskData.lastNudgedAt = numberToTimestamp(task.lastNudgedAt);
  if (task.followUpDismissedAt) taskData.followUpDismissedAt = numberToTimestamp(task.followUpDismissedAt);
  
  await setDoc(taskRef, taskData);
  return taskRef.id;
};

export const updateTask = async (taskId: string, updates: Partial<Task>): Promise<void> => {
  if (useMockData || !db) throw new Error('Firebase not configured');
  
  const updateData: any = {
    ...updates,
    lastActivityAt: Timestamp.now(),
  };
  
  // Convert timestamp fields
  if (updates.startedAt !== undefined) {
    updateData.startedAt = updates.startedAt ? numberToTimestamp(updates.startedAt) : null;
  }
  if (updates.completedAt !== undefined) {
    updateData.completedAt = updates.completedAt ? numberToTimestamp(updates.completedAt) : null;
  }
  if (updates.lastNudgedAt !== undefined) {
    updateData.lastNudgedAt = updates.lastNudgedAt ? numberToTimestamp(updates.lastNudgedAt) : null;
  }
  if (updates.followUpDismissedAt !== undefined) {
    updateData.followUpDismissedAt = updates.followUpDismissedAt ? numberToTimestamp(updates.followUpDismissedAt) : null;
  }
  
  await updateDoc(doc(db, 'tasks', taskId), updateData);
};

export const deleteTask = async (taskId: string): Promise<void> => {
  if (useMockData || !db) throw new Error('Firebase not configured');
  
  await deleteDoc(doc(db, 'tasks', taskId));
};

// BATCH OPERATIONS FOR TASKS

/**
 * Batch update multiple tasks atomically
 * All updates succeed or all fail together
 */
export const batchUpdateTasks = async (
  updates: Array<{ taskId: string; data: Partial<Task> }>
): Promise<void> => {
  if (useMockData || !db) throw new Error('Firebase not configured');
  
  if (updates.length === 0) return;
  
  addSentryBreadcrumb('Batch update tasks', { 
    count: updates.length, 
    taskIds: updates.map(u => u.taskId) 
  });
  
  await withRetry(async () => {
    const batch = writeBatch(db);
    
    updates.forEach(({ taskId, data }) => {
      const updateData: any = {
        ...data,
        lastActivityAt: Timestamp.now(),
      };
      
      // Convert timestamp fields
      if (data.startedAt !== undefined) {
        updateData.startedAt = data.startedAt ? numberToTimestamp(data.startedAt) : null;
      }
      if (data.completedAt !== undefined) {
        updateData.completedAt = data.completedAt ? numberToTimestamp(data.completedAt) : null;
      }
      if (data.lastNudgedAt !== undefined) {
        updateData.lastNudgedAt = data.lastNudgedAt ? numberToTimestamp(data.lastNudgedAt) : null;
      }
      if (data.followUpDismissedAt !== undefined) {
        updateData.followUpDismissedAt = data.followUpDismissedAt ? numberToTimestamp(data.followUpDismissedAt) : null;
      }
      
      batch.update(doc(db, 'tasks', taskId), updateData);
    });
    
    await batch.commit();
  }, 'batchUpdateTasks');
};

/**
 * Batch create multiple tasks atomically
 * Returns array of generated Firebase IDs
 */
export const batchCreateTasks = async (
  tasks: Array<Omit<Task, 'id'>>
): Promise<string[]> => {
  if (useMockData || !db) throw new Error('Firebase not configured');
  
  if (tasks.length === 0) return [];
  
  addSentryBreadcrumb('Batch create tasks', { count: tasks.length });
  
  const refs: any[] = [];
  
  await withRetry(async () => {
    const batch = writeBatch(db);
    refs.length = 0; // Clear refs array in case of retry
    
    tasks.forEach(task => {
      const taskRef = doc(collection(db, 'tasks'));
      refs.push(taskRef);
      
      const taskData: any = {
        ...task,
        lastActivityAt: Timestamp.now(),
      };
      
      // Convert timestamp fields
      if (task.startedAt) taskData.startedAt = numberToTimestamp(task.startedAt);
      if (task.completedAt) taskData.completedAt = numberToTimestamp(task.completedAt);
      if (task.lastNudgedAt) taskData.lastNudgedAt = numberToTimestamp(task.lastNudgedAt);
      if (task.followUpDismissedAt) taskData.followUpDismissedAt = numberToTimestamp(task.followUpDismissedAt);
      
      batch.set(taskRef, taskData);
    });
    
    await batch.commit();
  }, 'batchCreateTasks');
  
  return refs.map(ref => ref.id);
};

/**
 * Batch delete multiple tasks atomically
 */
export const batchDeleteTasks = async (taskIds: string[]): Promise<void> => {
  if (useMockData || !db) throw new Error('Firebase not configured');
  
  if (taskIds.length === 0) return;
  
  addSentryBreadcrumb('Batch delete tasks', { count: taskIds.length, taskIds });
  
  await withRetry(async () => {
    const batch = writeBatch(db);
    
    taskIds.forEach(taskId => {
      batch.delete(doc(db, 'tasks', taskId));
    });
    
    await batch.commit();
  }, 'batchDeleteTasks');
};

/**
 * Transaction for task creation with parent/sibling relationship updates
 * Ensures atomicity across task creation and related updates
 */
export const createTaskWithRelationships = async (
  newTask: Omit<Task, 'id'>,
  parentUpdate: { taskId: string; data: Partial<Task> },
  siblingUpdates: Array<{ taskId: string; data: Partial<Task> }>
): Promise<string> => {
  if (useMockData || !db) throw new Error('Firebase not configured');
  
  addSentryBreadcrumb('Create task with relationships', { 
    parentId: parentUpdate.taskId,
    siblingCount: siblingUpdates.length 
  });
  
  return await withRetry(async () => {
    return await runTransaction(db, async (transaction) => {
    // 1. Create new task
    const newTaskRef = doc(collection(db, 'tasks'));
    const taskData: any = {
      ...newTask,
      lastActivityAt: Timestamp.now(),
    };
    
    // Convert timestamp fields
    if (newTask.startedAt) taskData.startedAt = numberToTimestamp(newTask.startedAt);
    if (newTask.completedAt) taskData.completedAt = numberToTimestamp(newTask.completedAt);
    if (newTask.lastNudgedAt) taskData.lastNudgedAt = numberToTimestamp(newTask.lastNudgedAt);
    if (newTask.followUpDismissedAt) taskData.followUpDismissedAt = numberToTimestamp(newTask.followUpDismissedAt);
    
    transaction.set(newTaskRef, taskData);
    
    // 2. Update parent with new task ID
    const parentRef = doc(db, 'tasks', parentUpdate.taskId);
    const parentUpdateData: any = {
      ...parentUpdate.data,
      lastActivityAt: Timestamp.now(),
    };
    
    // Replace PLACEHOLDER with actual ID
    Object.keys(parentUpdateData).forEach(key => {
      if (parentUpdateData[key] === 'PLACEHOLDER') {
        parentUpdateData[key] = newTaskRef.id;
      }
    });
    
    transaction.update(parentRef, parentUpdateData);
    
    // 3. Update sibling positions
    siblingUpdates.forEach(({ taskId, data }) => {
      const siblingRef = doc(db, 'tasks', taskId);
      const updateData: any = {
        ...data,
        lastActivityAt: Timestamp.now(),
      };
      transaction.update(siblingRef, updateData);
    });
    
    return newTaskRef.id;
  });
  }, 'createTaskWithRelationships');
};

// BATCH OPERATIONS FOR PHASES

/**
 * Batch create multiple phases atomically
 * Returns array of generated Firebase IDs
 */
export const batchCreatePhases = async (
  phases: Array<Omit<Phase, 'id'>>
): Promise<string[]> => {
  if (useMockData || !db) throw new Error('Firebase not configured');
  
  if (phases.length === 0) return [];
  
  const batch = writeBatch(db);
  const refs: any[] = [];
  
  phases.forEach(phase => {
    const phaseRef = doc(collection(db, 'phases'));
    refs.push(phaseRef);
    batch.set(phaseRef, phase);
  });
  
  await batch.commit();
  return refs.map(ref => ref.id);
};

/**
 * Batch update multiple phases atomically
 */
export const batchUpdatePhases = async (
  updates: Array<{ phaseId: string; data: Partial<Phase> }>
): Promise<void> => {
  if (useMockData || !db) throw new Error('Firebase not configured');
  
  if (updates.length === 0) return;
  
  const batch = writeBatch(db);
  
  updates.forEach(({ phaseId, data }) => {
    batch.update(doc(db, 'phases', phaseId), data);
  });
  
  await batch.commit();
};

/**
 * Transaction for advancing phase (update completed phase + activate next + update project)
 */
export const advancePhaseTransaction = async (
  completedPhaseId: string,
  nextPhaseId: string | undefined,
  projectId: string | undefined,
  projectUpdates: Partial<Project>
): Promise<void> => {
  if (useMockData || !db) throw new Error('Firebase not configured');
  
  addSentryBreadcrumb('Advance phase transaction', { 
    completedPhaseId, 
    nextPhaseId, 
    projectId 
  });
  
  await withRetry(async () => {
    await runTransaction(db, async (transaction) => {
      // 1. Update completed phase
      const completedPhaseRef = doc(db, 'phases', completedPhaseId);
      transaction.update(completedPhaseRef, {
        status: PhaseStatus.COMPLETED,
        gatePassed: true
      });
      
      // 2. Activate next phase if exists
      if (nextPhaseId) {
        const nextPhaseRef = doc(db, 'phases', nextPhaseId);
        transaction.update(nextPhaseRef, {
          status: PhaseStatus.ACTIVE
        });
      }
      
      // 3. Update project if needed
      if (projectId && Object.keys(projectUpdates).length > 0) {
        const projectRef = doc(db, 'projects', projectId);
        transaction.update(projectRef, projectUpdates);
      }
    });
  }, 'advancePhaseTransaction');
};

// TICKETS
export const fetchTickets = async (userId?: string): Promise<Ticket[]> => {
  if (useMockData || !db) return [];
  
  try {
    const constraints: QueryConstraint[] = [];
    if (userId) {
      constraints.push(where('assigneeId', '==', userId));
    }
    constraints.push(orderBy('updatedAt', 'desc'));
    
    const q = query(collection(db, 'tickets'), ...constraints);
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: timestampToNumber(data.createdAt),
        updatedAt: timestampToNumber(data.updatedAt),
      };
    }) as Ticket[];
  } catch (error) {
    console.error('Error fetching tickets:', error);
    return [];
  }
};

export const createTicket = async (ticket: Omit<Ticket, 'id' | 'createdAt' | 'updatedAt' | 'status'>): Promise<string> => {
  if (useMockData || !db) throw new Error('Firebase not configured');
  
  // Remove undefined fields to avoid Firestore errors
  const cleanedTicket: any = {};
  Object.keys(ticket).forEach(key => {
    const value = (ticket as any)[key];
    if (value !== undefined) {
      cleanedTicket[key] = value;
    }
  });
  
  const ticketRef = doc(collection(db, 'tickets'));
  await setDoc(ticketRef, {
    ...cleanedTicket,
    status: 'OPEN',
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
  return ticketRef.id;
};

export const updateTicket = async (ticketId: string, updates: Partial<Ticket>): Promise<void> => {
  if (useMockData || !db) throw new Error('Firebase not configured');
  
  // Remove undefined fields to avoid Firestore errors
  const cleanedUpdates: any = {};
  Object.keys(updates).forEach(key => {
    const value = (updates as any)[key];
    if (value !== undefined) {
      cleanedUpdates[key] = value;
    }
  });
  
  await updateDoc(doc(db, 'tickets', ticketId), {
    ...cleanedUpdates,
    updatedAt: Timestamp.now(),
  });
};

// TEMPLATES
export const fetchTemplates = async (): Promise<Template[]> => {
  if (useMockData || !db) return [];
  
  try {
    const q = query(collection(db, 'templates'), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: timestampToNumber(data.createdAt),
        updatedAt: timestampToNumber(data.updatedAt),
      };
    }) as Template[];
  } catch (error) {
    console.error('Error fetching templates:', error);
    return [];
  }
};

export const createTemplate = async (template: Omit<Template, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  if (useMockData || !db) throw new Error('Firebase not configured');
  
  const templateRef = doc(collection(db, 'templates'));
  await setDoc(templateRef, {
    ...template,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
  return templateRef.id;
};

export const updateTemplate = async (templateId: string, updates: Partial<Template>): Promise<void> => {
  if (useMockData || !db) throw new Error('Firebase not configured');
  
  await updateDoc(doc(db, 'templates', templateId), {
    ...updates,
    updatedAt: Timestamp.now(),
  });
};

export const deleteTemplate = async (templateId: string): Promise<void> => {
  if (useMockData || !db) throw new Error('Firebase not configured');
  
  await deleteDoc(doc(db, 'templates', templateId));
};

// CUSTOM CALENDARS
export const fetchCustomCalendars = async (userId?: string): Promise<CustomCalendar[]> => {
  if (useMockData || !db || !userId) return [];
  
  try {
    // Query calendars using three separate array-contains queries that respect security rules
    const [creatorDocs, editorDocs, viewerDocs] = await Promise.all([
      getDocs(query(collection(db, 'customCalendars'), where('creatorId', '==', userId))),
      getDocs(query(collection(db, 'customCalendars'), where('editorIds', 'array-contains', userId))),
      getDocs(query(collection(db, 'customCalendars'), where('viewerIds', 'array-contains', userId)))
    ]);
    
    // Merge and deduplicate by ID
    const calendarMap = new Map<string, CustomCalendar>();
    [...creatorDocs.docs, ...editorDocs.docs, ...viewerDocs.docs].forEach(doc => {
      if (!calendarMap.has(doc.id)) {
        calendarMap.set(doc.id, {
          id: doc.id,
          ...doc.data(),
          updatedAt: timestampToNumber(doc.data().updatedAt),
        } as CustomCalendar);
      }
    });
    
    return Array.from(calendarMap.values());
  } catch (error) {
    console.error('Error fetching calendars:', error);
    return [];
  }
};

export const createCustomCalendar = async (calendar: Omit<CustomCalendar, 'id' | 'updatedAt'>): Promise<string> => {
  if (useMockData || !db) throw new Error('Firebase not configured');
  
  const calendarRef = doc(collection(db, 'customCalendars'));
  await setDoc(calendarRef, {
    ...calendar,
    updatedAt: Timestamp.now(),
  });
  return calendarRef.id;
};

export const updateCustomCalendar = async (calendarId: string, updates: Partial<CustomCalendar>): Promise<void> => {
  if (useMockData || !db) throw new Error('Firebase not configured');
  
  await updateDoc(doc(db, 'customCalendars', calendarId), {
    ...updates,
    updatedAt: Timestamp.now(),
  });
};

export const deleteCustomCalendar = async (calendarId: string): Promise<void> => {
  if (useMockData || !db) throw new Error('Firebase not configured');
  
  await deleteDoc(doc(db, 'customCalendars', calendarId));
};

// PROJECT ROLES
export const fetchProjectRoles = async (): Promise<ProjectRole[]> => {
  if (useMockData || !db) return [];
  
  try {
    const snapshot = await getDocs(collection(db, 'projectRoles'));
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: timestampToNumber(data.createdAt),
      };
    }) as ProjectRole[];
  } catch (error) {
    console.error('Error fetching project roles:', error);
    return [];
  }
};

export const createProjectRole = async (role: Omit<ProjectRole, 'id' | 'createdAt'>): Promise<string> => {
  if (useMockData || !db) throw new Error('Firebase not configured');
  
  const roleRef = doc(collection(db, 'projectRoles'));
  await setDoc(roleRef, {
    ...role,
    createdAt: Timestamp.now(),
  });
  return roleRef.id;
};

export const updateProjectRole = async (roleId: string, updates: Partial<ProjectRole>): Promise<void> => {
  if (useMockData || !db) throw new Error('Firebase not configured');
  
  await updateDoc(doc(db, 'projectRoles', roleId), updates);
};

export const deleteProjectRole = async (roleId: string): Promise<void> => {
  if (useMockData || !db) throw new Error('Firebase not configured');
  
  await deleteDoc(doc(db, 'projectRoles', roleId));
};

// USERS
export const fetchUsers = async (): Promise<User[]> => {
  if (useMockData || !db) return [];
  
  try {
    const snapshot = await getDocs(collection(db, 'users'));
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as User[];
  } catch (error) {
    console.error('Error fetching users:', error);
    return [];
  }
};

export const getUser = async (userId: string): Promise<User | null> => {
  if (useMockData || !db) return null;
  
  try {
    const docSnap = await getDoc(doc(db, 'users', userId));
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as User;
    }
    return null;
  } catch (error) {
    console.error('Error fetching user:', error);
    return null;
  }
};

export const updateUser = async (userId: string, updates: Partial<User>): Promise<void> => {
  if (useMockData || !db) throw new Error('Firebase not configured');
  
  await updateDoc(doc(db, 'users', userId), updates);
};

// COMMENTS
export const addCommentToTask = async (taskId: string, comment: Comment): Promise<void> => {
  if (useMockData || !db) throw new Error('Firebase not configured');
  
  const taskRef = doc(db, 'tasks', taskId);
  const taskSnap = await getDoc(taskRef);
  
  if (taskSnap.exists()) {
    const currentComments = taskSnap.data().comments || [];
    await updateDoc(taskRef, {
      comments: [...currentComments, comment],
      lastActivityAt: Timestamp.now(),
    });
  }
};

export const updateTaskComment = async (taskId: string, commentId: string, updates: Partial<Comment>): Promise<void> => {
  if (useMockData || !db) throw new Error('Firebase not configured');
  
  const taskRef = doc(db, 'tasks', taskId);
  const taskSnap = await getDoc(taskRef);
  
  if (taskSnap.exists()) {
    const comments = taskSnap.data().comments || [];
    const updatedComments = comments.map((c: Comment) =>
      c.id === commentId ? { ...c, ...updates } : c
    );
    await updateDoc(taskRef, {
      comments: updatedComments,
      lastActivityAt: Timestamp.now(),
    });
  }
};

// FILES
export const fetchFiles = async (projectId?: string): Promise<FileAsset[]> => {
  if (useMockData || !db) return [];
  
  try {
    const constraints: QueryConstraint[] = [];
    if (projectId) {
      constraints.push(where('projectId', '==', projectId));
    }
    constraints.push(orderBy('uploadDate', 'desc'));
    
    const q = query(collection(db, 'files'), ...constraints);
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as FileAsset[];
  } catch (error) {
    console.error('Error fetching files:', error);
    return [];
  }
};

export const createFile = async (file: Omit<FileAsset, 'id'>): Promise<string> => {
  if (useMockData || !db) throw new Error('Firebase not configured');
  
  try {
    const fileRef = doc(collection(db, 'files'));
    await setDoc(fileRef, {
      ...file,
      createdAt: Timestamp.now(),
    });
    return fileRef.id;
  } catch (error) {
    handleError(error);
    throw error;
  }
};

export const updateFile = async (fileId: string, updates: Partial<FileAsset>): Promise<void> => {
  if (useMockData || !db) throw new Error('Firebase not configured');
  
  try {
    await updateDoc(doc(db, 'files', fileId), {
      ...updates,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    handleError(error);
    throw error;
  }
};

export const deleteFile = async (fileId: string): Promise<void> => {
  if (useMockData || !db) throw new Error('Firebase not configured');
  
  try {
    await deleteDoc(doc(db, 'files', fileId));
  } catch (error) {
    handleError(error);
    throw error;
  }
};

// REAL-TIME SUBSCRIPTIONS
export const subscribeToProjects = (
  userId: string | undefined,
  callback: (projects: Project[]) => void
): Unsubscribe | null => {
  if (useMockData || !db) return null;
  
  try {
    const constraints: QueryConstraint[] = [];
    if (userId) {
      constraints.push(where('assignedUserIds', 'array-contains', userId));
    }
    constraints.push(orderBy('startDate', 'desc'));
    
    const q = query(collection(db, 'projects'), ...constraints);
    return onSnapshot(q, (snapshot) => {
      const projects = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        startDate: doc.data().startDate || '',
        launchDate: doc.data().launchDate || '',
      })) as Project[];
      callback(projects);
    });
  } catch (error) {
    console.error('Error subscribing to projects:', error);
    return null;
  }
};

export const subscribeToTasks = (
  projectId: string | undefined,
  phaseId: string | undefined,
  callback: (tasks: Task[]) => void
): Unsubscribe | null => {
  if (useMockData || !db) return null;
  
  try {
    const constraints: QueryConstraint[] = [];
    if (projectId) {
      constraints.push(where('projectId', '==', projectId));
    }
    if (phaseId) {
      constraints.push(where('phaseId', '==', phaseId));
    }
    
    const q = query(collection(db, 'tasks'), ...constraints);
    return onSnapshot(q, (snapshot) => {
      const tasks = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          lastActivityAt: timestampToNumber(data.lastActivityAt),
          startedAt: data.startedAt ? timestampToNumber(data.startedAt) : undefined,
          completedAt: data.completedAt ? timestampToNumber(data.completedAt) : undefined,
          lastNudgedAt: data.lastNudgedAt ? timestampToNumber(data.lastNudgedAt) : undefined,
          followUpDismissedAt: data.followUpDismissedAt ? timestampToNumber(data.followUpDismissedAt) : undefined,
        };
      }) as Task[];
      callback(tasks);
    });
  } catch (error) {
    console.error('Error subscribing to tasks:', error);
    return null;
  }
};

// Subscribe to my assigned tasks
export const subscribeToMyAssignedTasks = (
  userId: string,
  callback: (tasks: Task[]) => void
): Unsubscribe | null => {
  if (useMockData || !db) return null;
  
  try {
    const q = query(
      collection(db, 'tasks'),
      where('assigneeIds', 'array-contains', userId)
    );
    
    return onSnapshot(q, (snapshot) => {
      const tasks = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          lastActivityAt: timestampToNumber(data.lastActivityAt),
          startedAt: data.startedAt ? timestampToNumber(data.startedAt) : undefined,
          completedAt: data.completedAt ? timestampToNumber(data.completedAt) : undefined,
          lastNudgedAt: data.lastNudgedAt ? timestampToNumber(data.lastNudgedAt) : undefined,
          followUpDismissedAt: data.followUpDismissedAt ? timestampToNumber(data.followUpDismissedAt) : undefined,
        };
      }) as Task[];
      callback(tasks);
    });
  } catch (error) {
    console.error('Error subscribing to my assigned tasks:', error);
    return null;
  }
};

// Subscribe to template tasks
export const subscribeToTemplateTasks = (
  templateId: string,
  callback: (tasks: Task[]) => void
): Unsubscribe | null => {
  if (useMockData || !db) return null;
  
  try {
    const q = query(
      collection(db, 'tasks'),
      where('templateId', '==', templateId),
      where('projectId', '==', null)
    );
    
    return onSnapshot(q, (snapshot) => {
      const tasks = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          lastActivityAt: timestampToNumber(data.lastActivityAt),
          startedAt: data.startedAt ? timestampToNumber(data.startedAt) : undefined,
          completedAt: data.completedAt ? timestampToNumber(data.completedAt) : undefined,
          lastNudgedAt: data.lastNudgedAt ? timestampToNumber(data.lastNudgedAt) : undefined,
          followUpDismissedAt: data.followUpDismissedAt ? timestampToNumber(data.followUpDismissedAt) : undefined,
        };
      }) as Task[];
      callback(tasks);
    });
  } catch (error) {
    console.error('Error subscribing to template tasks:', error);
    return null;
  }
};

// Subscribe to tickets
export const subscribeToTickets = (
  userId: string,
  callback: (tickets: Ticket[]) => void
): Unsubscribe | null => {
  if (useMockData || !db) return null;
  
  try {
    const q = query(
      collection(db, 'tickets'),
      where('assigneeId', '==', userId),
      orderBy('updatedAt', 'desc')
    );
    
    return onSnapshot(q, (snapshot) => {
      const tickets = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: timestampToNumber(data.createdAt),
          updatedAt: timestampToNumber(data.updatedAt),
        };
      }) as Ticket[];
      callback(tickets);
    });
  } catch (error) {
    console.error('Error subscribing to tickets:', error);
    return null;
  }
};
